import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Check, X, Lightbulb, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { specificityCheck } from "@/lib/format";
import { useMeta } from "@/context/MetaContext";

function Hint({ ok, label }) {
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", ok ? "text-patria-green" : "text-patria-muted")}>
      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </span>
  );
}

export function SpecificityField({
  label,
  value,
  onChange,
  minLen = 40,
  requireDate = false,
  placeholder,
  helper,
  testId,
  single = false,
  required = true,
}) {
  const [showExamples, setShowExamples] = useState(false);
  const { data: meta } = useMeta();
  const check = specificityCheck(value, { minLen, requireDate });
  const InputEl = single ? Input : Textarea;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-patria-ink font-medium">
          {label} {required && <span className="text-patria-terracotta">*</span>}
        </Label>
        <button
          type="button"
          onClick={() => setShowExamples((s) => !s)}
          data-testid={`${testId}-examples-toggle`}
          className="inline-flex items-center gap-1 text-xs text-patria-terracottaDeep hover:text-patria-terracotta transition-colors"
        >
          <Lightbulb className="h-3.5 w-3.5" /> Examples
        </button>
      </div>
      {helper && <p className="text-xs text-patria-muted leading-relaxed">{helper}</p>}

      {showExamples && meta?.examples && (
        <div className="rounded-xl bg-patria-sage/60 p-3 space-y-2 text-xs" data-testid={`${testId}-examples`}>
          {meta.examples.map((ex, i) => (
            <div key={i} className="grid grid-cols-[16px_1fr] gap-x-2 gap-y-0.5">
              <X className="h-3.5 w-3.5 text-patria-terracottaDeep mt-0.5" />
              <span className="text-patria-muted">"{ex.vague}"</span>
              <Check className="h-3.5 w-3.5 text-patria-green mt-0.5" />
              <span className="text-patria-ink">{ex.specific}</span>
            </div>
          ))}
        </div>
      )}

      <InputEl
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={single ? undefined : 3}
        data-testid={testId}
        className={cn(
          "bg-patria-surface border-patria-muted/30 focus-visible:ring-patria-green text-patria-ink",
          value && (check.ok ? "border-patria-green/50" : "border-patria-terracotta/50")
        )}
      />
      {required && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Hint ok={check.longEnough} label={`${check.length}/${minLen} chars`} />
          {requireDate && <Hint ok={check.hasDate} label="includes a date" />}
          <Hint ok={check.notVague} label="not vague" />
        </div>
      )}
    </div>
  );
}

export function PlainDateField({ label, value, onChange, testId, required = true }) {
  return (
    <div className="space-y-2">
      <Label className="text-patria-ink font-medium inline-flex items-center gap-1.5">
        <CalendarClock className="h-3.5 w-3.5 text-patria-muted" />
        {label} {required && <span className="text-patria-terracotta">*</span>}
      </Label>
      <Input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-patria-surface border-patria-muted/30 focus-visible:ring-patria-green text-patria-ink"
      />
    </div>
  );
}
